// @flow
import fromEntries from 'lodash/fp/fromPairs';
import invariant from 'invariant';

export { default as isEqual } from 'lodash/fp/isEqual';

/**
 * Curried getter for an object property.
 *
 * NOTE: `P` _must_ be a literal string type for this to actually be safe,
 * otherwise the property getter will only return (basically) `any`.
 *
 */
export const propertyGetter =
  <+P: string>(key: P): (<O: { +[string]: mixed }>(O) => $ElementType<O, P>) =>
  <O: { +[string]: mixed }>(obj: O): $ElementType<O, P> =>
    obj[key];

export const values = <T>(o: { +[string]: T }): $ReadOnlyArray<T> =>
  Object.keys(o).map((k) => o[k]);

export const entries = <K: string, T>(o: { +[K]: T }): $ReadOnlyArray<[K, T]> =>
  Object.keys(o).map((k) => [k, o[k]]);

export const fromPairs = <K: string, T>(
  list: $ReadOnlyArray<[K, T]>,
): { +[K]: T } =>
  list.reduce(
    (acc: { +[K]: T }, [key, value]: [K, T]): { +[K]: T } => ({
      ...acc,
      [key]: value,
    }),
    ({ ...null }: { +[K]: T }),
  );

export const withoutProperty = <O: { +[string]: mixed }>(
  property: string,
  o: O,
): O => ((fromEntries(entries(o).filter(([k]) => k !== property)): any): O);

export const valueAtPath =
  (path: $ReadOnlyArray<string>): ((o: { ... }) => mixed) =>
  (o: { ... }): mixed =>
    path.reduce(
      (acc, current) => (acc && typeof acc === 'object' ? acc[current] : null),
      o,
    );

export const withValueAtPath = <V, O: { +[string]: V }, T>(
  path: $ReadOnlyArray<string>,
  value: T,
  o: O,
): { +[string]: V | T } => {
  invariant(path.length > 0, 'The path must not be length === 0!');

  const head = path[0];
  const property = path[1] || head;
  const body = path.slice(1);

  if (body.length === 0) return { ...o, [property]: value };

  const target = o[head];

  invariant(
    target === null ||
      typeof target === 'undefined' ||
      typeof target === 'object',
    `target must be an object, null, or undefined, but it is ${String(
      target,
    )}!`,
  );

  return { ...o, [head]: withValueAtPath(body, value, target || { ...null }) };
};
